import { CommonModule, DatePipe, DecimalPipe } from '@angular/common';
import { ChangeDetectionStrategy, Component, computed, input } from '@angular/core';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import { ProductionKeyPerformanceIndicatorEvaluationEnum } from 'src/app/enums/production-key-performance-indicator-evaluation.enum';
import { ProductionKeyPerformanceIndicatorEvaluation } from 'src/app/models/production-key-performance-indicator-evaluation.model';
import {
  CompanyProductPhaseTimeComplianceGeneralKpiDetail,
  CompanyProductPhaseTimeCompliancePhaseTypeSummary,
  CompanyProductPhaseTimeComplianceWeekDetail,
} from 'src/app/models/company-product-phase-time-compliance-kpi-data.model';
import { HourDecimalToHoursMinutesPipe } from 'src/app/pipes/hour-decimal-to-hours-minutes.pipe';

@Component({
  selector: 'app-company-product-phase-time-compliance-details',
  imports: [CommonModule, DatePipe, DecimalPipe, HourDecimalToHoursMinutesPipe, MatIconModule, MatTooltipModule],
  templateUrl: './company-product-phase-time-compliance-details.component.html',
  styleUrl: './company-product-phase-time-compliance-details.component.css',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CompanyProductPhaseTimeComplianceDetailsComponent {
  currentMonthEvaluation = input<ProductionKeyPerformanceIndicatorEvaluation | null>(null);

  private kpiDetail = computed((): CompanyProductPhaseTimeComplianceGeneralKpiDetail | null => {
    const month = this.currentMonthEvaluation();
    if (month === null) {
      return null;
    }

    return CompanyProductPhaseTimeComplianceDetailsComponent.parseKpiDetail(month.data);
  });

  phaseTypeSummaries = computed((): CompanyProductPhaseTimeCompliancePhaseTypeSummary[] => this.kpiDetail()?.phase_type_summaries ?? []);

  emptyMessage = computed((): string => {
    const evaluation = this.currentMonthEvaluation()?.evaluation;

    if (evaluation === ProductionKeyPerformanceIndicatorEvaluationEnum.NotCalculable) {
      return 'Aún no hay semanas cerradas en este periodo.';
    }

    return 'Sin datos para este periodo.';
  });

  private static parseKpiDetail(raw: unknown): CompanyProductPhaseTimeComplianceGeneralKpiDetail | null {
    if (raw === null || raw === undefined) {
      return null;
    }

    let parsed: unknown = raw;
    if (typeof raw === 'string') {
      try {
        parsed = JSON.parse(raw) as unknown;
      } catch {
        return null;
      }
    }

    return CompanyProductPhaseTimeComplianceDetailsComponent.isGeneralKpiDetail(parsed) ? parsed : null;
  }

  private static isGeneralKpiDetail(value: unknown): value is CompanyProductPhaseTimeComplianceGeneralKpiDetail {
    if (value === null || typeof value !== 'object' || Array.isArray(value)) {
      return false;
    }

    const candidate = value as CompanyProductPhaseTimeComplianceGeneralKpiDetail;
    const summaries = candidate.phase_type_summaries;

    if (summaries !== undefined && !Array.isArray(summaries)) {
      return false;
    }

    if (Array.isArray(summaries)) {
      return summaries.every(summary => CompanyProductPhaseTimeComplianceDetailsComponent.isPhaseTypeSummary(summary));
    }

    return true;
  }

  private static isPhaseTypeSummary(value: unknown): value is CompanyProductPhaseTimeCompliancePhaseTypeSummary {
    if (value === null || typeof value !== 'object') {
      return false;
    }

    const summary = value as CompanyProductPhaseTimeCompliancePhaseTypeSummary;

    if (
      typeof summary.company_product_phase_type_name !== 'string' ||
      typeof summary.done_hours !== 'number' ||
      typeof summary.expected_hours !== 'number'
    ) {
      return false;
    }

    if (summary.weeks === undefined) {
      return true;
    }

    if (!Array.isArray(summary.weeks)) {
      return false;
    }

    return summary.weeks.every(week => CompanyProductPhaseTimeComplianceDetailsComponent.isWeekDetail(week));
  }

  private static isWeekDetail(value: unknown): value is CompanyProductPhaseTimeComplianceWeekDetail {
    if (value === null || typeof value !== 'object') {
      return false;
    }

    const week = value as CompanyProductPhaseTimeComplianceWeekDetail;

    return (
      typeof week.week_start === 'string' &&
      typeof week.week_end === 'string' &&
      typeof week.done_hours === 'number' &&
      typeof week.expected_hours === 'number' &&
      typeof week.is_correct === 'boolean' &&
      typeof week.status_tooltip === 'string'
    );
  }
}
